import { SelectGroupType, SelectOptionType } from '../types/select';

/**
 * Maps an array of any type to SelectOption array using the provided mapper function
 * @param items Array of source items
 * @param mapper Function to map source items to SelectOption format
 * @returns Array of SelectOption objects
 */
export function mapToSelectOptions<T>(
  items: T[],
  mapper: (item: T) => SelectOptionType | { value: string; label: string },
): SelectOptionType[] {
  return items.map((item) => {
    const mapped = mapper(item);
    // Ensure we return a proper SelectOption
    return {
      value: String(mapped.value),
      label: String(mapped.label),
      disabled: 'disabled' in mapped ? mapped.disabled : undefined,
    };
  });
}

/**
 * Maps an array of any type to SelectGroupType array using the provided mapper function
 * @param groups Array of source group items
 * @param mapper Function to map source groups to SelectGroupType format
 * @returns Array of SelectGroupType objects
 */
export function mapToSelectGroups<T>(
  groups: T[],
  mapper: (group: T) =>
    | SelectGroupType
    | {
        label: string;
        key?: string;
        options: Array<SelectOptionType | { value: string; label: string }>;
      },
): SelectGroupType[] {
  return groups.map((group) => {
    const mapped = mapper(group);

    // Convert options to standard format if needed
    const standardOptions = mapped.options.map((option) => ({
      value: String(option.value),
      label: String(option.label),
      disabled: 'disabled' in option ? option.disabled : undefined,
    }));

    // Return a standardized SelectGroupType
    return {
      label: mapped.label,
      key: mapped.key || mapped.label,
      options: standardOptions,
    };
  });
}

/**
 * Creates a SelectOption object
 * @param value Option value
 * @param label Option label
 * @param disabled Whether the option is disabled
 * @returns SelectOption object
 */
export function createSelectOption(
  value: string,
  label: string,
  disabled?: boolean,
): SelectOptionType {
  return { value, label, disabled };
}

/**
 * Creates a SelectGroupType object
 * @param label Group label
 * @param options Array of SelectOption objects
 * @param key Optional unique identifier for the group
 * @returns SelectGroupType object
 */
export function createSelectGroup(
  label: string,
  options: SelectOptionType[],
  key?: string,
): SelectGroupType {
  return { label, options, key: key || label };
}
